/**
 * MA-ARCH-137: Actionable Recommendations Component
 * 
 * Extends optimization recommendations with concrete, approvable actions.
 * This is the bridge between analytics and operational leverage.
 * 
 * CRITICAL: Human-approved execution only - no autonomous actions.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, 
  Wrench, 
  PauseCircle, 
  Sparkles, 
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Users,
  Target,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  Play,
  X,
  Clock,
  Loader2,
  Info,
  ArrowUpCircle,
  Copy,
  Flag,
  Zap,
  FileText
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '@/lib/api';
import { WarningBanner, HELPER_DEFINITIONS } from './HelperText';

// =============================================================================
// CONFIGURATION
// =============================================================================

const recommendationConfig = {
  scale: {
    icon: TrendingUp,
    label: 'SCALE',
    color: 'emerald',
    bgClass: 'bg-emerald-500/10 border-emerald-500/40',
    textClass: 'text-emerald-400',
    badgeClass: 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/50',
    description: 'Increase traffic to this high performer',
    actionVerb: 'Scale up',
  },
  fix: {
    icon: Wrench,
    label: 'FIX',
    color: 'amber',
    bgClass: 'bg-amber-500/10 border-amber-500/40',
    textClass: 'text-amber-400',
    badgeClass: 'bg-amber-500/30 text-amber-300 border border-amber-400/50',
    description: 'Address issues to improve performance',
    actionVerb: 'Investigate',
  },
  pause: {
    icon: PauseCircle,
    label: 'PAUSE',
    color: 'red',
    bgClass: 'bg-red-500/10 border-red-500/40',
    textClass: 'text-red-400',
    badgeClass: 'bg-red-500/30 text-red-300 border border-red-400/50',
    description: 'Consider stopping or restructuring',
    actionVerb: 'Pause',
  },
};

const confidenceConfig = {
  high: {
    icon: ShieldCheck,
    label: 'High',
    bgClass: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    iconClass: 'text-emerald-400',
    tooltip: 'Strong data volume - act with confidence',
  },
  medium: {
    icon: ShieldAlert,
    label: 'Medium',
    bgClass: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
    iconClass: 'text-amber-400',
    tooltip: 'Moderate data volume - consider acting',
  },
  low: {
    icon: ShieldQuestion,
    label: 'Low',
    bgClass: 'bg-red-500/20 text-red-300 border border-red-500/30',
    iconClass: 'text-red-400',
    tooltip: 'Limited data - review before acting',
  },
};

const actionTypeIcons = {
  increase_traffic: ArrowUpCircle,
  duplicate_campaign: Copy,
  mark_scaling_candidate: Flag,
  align_script: Zap,
  review_impulse_alignment: Target,
  review_show_rate: FileText,
  switch_script: FileText,
  pause_campaigns: PauseCircle,
  mark_pause_candidate: Flag,
  stop_traffic: X,
};

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function fetchOptimizationActions(params) {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.campaignId) queryParams.append('campaign_id', params.campaignId);
  if (params.profitPathId) queryParams.append('profit_path_id', params.profitPathId);
  if (params.workspaceId) queryParams.append('workspace_id', params.workspaceId);
  if (params.channel) queryParams.append('channel', params.channel);
  if (params.vertical) queryParams.append('vertical', params.vertical);
  if (params.tenantId) queryParams.append('tenant_id', params.tenantId);
  
  const response = await api.get(`/analytics/optimization-actions?${queryParams}`);
  return response.data;
}

async function approveAction(actionId, userId) {
  const response = await api.post('/actions/approve', {
    action_id: actionId,
    approved_by: userId,
  });
  return response.data;
}

async function dismissAction(actionId) {
  const response = await api.post('/actions/dismiss', {
    action_id: actionId,
  });
  return response.data;
}

// =============================================================================
// ACTION CARD COMPONENT
// =============================================================================

function ActionButton({ action, onApprove, onDismiss, isApproving, isDismissing }) {
  const ActionIcon = actionTypeIcons[action.action_type] || Play;
  
  return (
    <div className="flex items-center justify-between p-2.5 bg-slate-800/40 rounded border border-slate-700/50 group hover:border-slate-600/50 transition-colors">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <ActionIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <span className="text-sm text-slate-300 truncate">{action.label}</span>
      </div>
      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
          onClick={onApprove}
          disabled={isApproving || isDismissing}
        >
          {isApproving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Approve
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-slate-400 hover:text-slate-300 hover:bg-slate-700/50"
          onClick={onDismiss}
          disabled={isApproving || isDismissing}
        >
          {isDismissing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// RECOMMENDATION CARD WITH ACTIONS
// =============================================================================

function ActionableRecommendationCard({ recommendation, onActionApproved, onActionDismissed, userId }) {
  const [expanded, setExpanded] = useState(false);
  const [approvingActionId, setApprovingActionId] = useState(null);
  const [dismissingActionId, setDismissingActionId] = useState(null);
  
  const config = recommendationConfig[recommendation.type];
  const confConfig = confidenceConfig[recommendation.confidence];
  const Icon = config.icon;
  const ConfidenceIcon = confConfig.icon;
  
  const handleApprove = async (actionId) => {
    setApprovingActionId(actionId);
    try {
      await approveAction(actionId, userId);
      onActionApproved?.(actionId);
    } catch (error) {
      console.error('Failed to approve action:', error);
    } finally {
      setApprovingActionId(null);
    }
  };
  
  const handleDismiss = async (actionId) => {
    setDismissingActionId(actionId);
    try {
      await dismissAction(actionId);
      onActionDismissed?.(actionId);
    } catch (error) {
      console.error('Failed to dismiss action:', error);
    } finally {
      setDismissingActionId(null);
    }
  };
  
  const hasLowConfidence = recommendation.confidence === 'low';
  const actionsWithIds = recommendation.actions.map((action, idx) => ({
    ...action,
    id: recommendation.action_ids[idx] || `temp_${idx}`,
  }));
  
  return (
    <div 
      className={cn(
        "p-4 rounded-lg border-2 transition-all",
        config.bgClass,
        expanded && "ring-1 ring-white/10"
      )}
      data-testid={`actionable-recommendation-${recommendation.type}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={cn("p-2.5 rounded-lg", config.badgeClass)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn("text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded", config.badgeClass)}>
                {config.label}
              </span>
              <span 
                className={cn("flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-medium cursor-help", confConfig.bgClass)}
                title={confConfig.tooltip}
              >
                <ConfidenceIcon className={cn("h-3 w-3", confConfig.iconClass)} />
                {confConfig.label}
              </span>
            </div>
            <h4 className="text-lg font-semibold text-white mt-1" data-testid="message-variant">
              {recommendation.message_variant}
            </h4>
          </div>
        </div>
        {recommendation.profit_path_name && (
          <span className="text-xs text-slate-400 bg-slate-800/50 px-2 py-1 rounded">
            {recommendation.profit_path_name}
          </span>
        )}
      </div>
      
      {/* Decision context - WHY */}
      <div className="mb-3 p-2.5 bg-slate-800/40 rounded border border-slate-700/50">
        <p className="text-sm text-slate-300">
          <span className={cn("font-medium", config.textClass)}>{config.actionVerb}:</span>{' '}
          {recommendation.reason}
        </p>
      </div>
      
      {/* Low confidence warning */}
      {hasLowConfidence && (
        <WarningBanner
          severity="info"
          icon={Info}
          message="Limited data - review before acting on these recommendations."
          compact
          className="mb-3"
        />
      )}
      
      {/* Key metrics */}
      <div className="flex flex-wrap gap-2 text-xs mb-3">
        {recommendation.supporting_metrics.revenue_per_lead !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <DollarSign className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-slate-400">Rev/Lead:</span>
            <span className="text-white font-semibold">
              ${recommendation.supporting_metrics.revenue_per_lead.toLocaleString()}
            </span>
          </div>
        )}
        {recommendation.supporting_metrics.leads !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <Users className="h-3.5 w-3.5 text-blue-400" />
            <span className="text-slate-400">Leads:</span>
            <span className="text-white font-semibold">
              {recommendation.supporting_metrics.leads}
            </span>
          </div>
        )}
        {recommendation.supporting_metrics.alignment_rate !== undefined && (
          <div className="flex items-center gap-1.5 bg-slate-800/60 px-2.5 py-1.5 rounded border border-slate-700/50">
            <Target className="h-3.5 w-3.5 text-violet-400" />
            <span className="text-slate-400">Alignment:</span>
            <span className="text-white font-semibold">
              {recommendation.supporting_metrics.alignment_rate}%
            </span>
          </div>
        )}
      </div>
      
      {/* Actions section */}
      <div className="border-t border-slate-700/50 pt-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 transition-colors w-full"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span className="font-medium">
            {recommendation.actions.length} available action{recommendation.actions.length !== 1 ? 's' : ''}
          </span>
        </button>
        
        {expanded && (
          <div className="mt-3 space-y-2">
            {actionsWithIds.map((action) => (
              <ActionButton
                key={action.id}
                action={action}
                onApprove={() => handleApprove(action.id)}
                onDismiss={() => handleDismiss(action.id)}
                isApproving={approvingActionId === action.id}
                isDismissing={dismissingActionId === action.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// QUEUE SUMMARY BADGE
// =============================================================================

function QueueSummaryBadge({ summary }) {
  if (!summary || summary.total === 0) return null;
  
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="queue-summary">
      {summary.approved > 0 && (
        <span className="flex items-center gap-1 text-emerald-400">
          <CheckCircle2 className="h-3 w-3" />
          {summary.approved} approved
        </span>
      )}
      {summary.proposed > 0 && (
        <span className="flex items-center gap-1 text-amber-400">
          <Clock className="h-3 w-3" />
          {summary.proposed} pending
        </span>
      )}
      {summary.dismissed > 0 && (
        <span className="flex items-center gap-1 text-slate-400">
          <X className="h-3 w-3" />
          {summary.dismissed} dismissed
        </span>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ActionableRecommendations({
  period = '30d',
  campaignId,
  profitPathId,
  workspaceId,
  channel,
  vertical,
  tenantId,
  maxRecommendations = 5,
  userId = 'unknown',
}) {
  const queryClient = useQueryClient();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['optimization-actions', period, campaignId, profitPathId, workspaceId, channel, vertical, tenantId],
    queryFn: () => fetchOptimizationActions({
      period,
      campaignId,
      profitPathId,
      workspaceId,
      channel,
      vertical,
      tenantId,
    }),
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
  });
  
  const handleActionApproved = (actionId) => {
    // Refetch to update the queue summary
    refetch();
  };
  
  const handleActionDismissed = (actionId) => {
    // Refetch to update the queue summary
    refetch();
  };
  
  if (isLoading) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading recommendations...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardContent className="py-12 text-center">
          <Lightbulb className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">Recommendations Unavailable</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Unable to load optimization recommendations at this time.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const { recommendations = [], summary = {}, queue_summary = {} } = data || {};
  const displayRecommendations = recommendations.slice(0, maxRecommendations);
  const hasRecommendations = recommendations.length > 0;
  const hasLowConfidenceRecs = recommendations.some(r => r.confidence === 'low');
  
  return (
    <Card className="bg-slate-900/50 border-slate-700/50" data-testid="actionable-recommendations">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-400" />
              Actionable Recommendations
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              {HELPER_DEFINITIONS.optimization_recommendations} • Approve to add to queue
            </p>
          </div>
          <div className="flex items-center gap-3">
            <QueueSummaryBadge summary={queue_summary} />
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>{summary.total_variants || 0} variants</span>
              <span className="text-slate-600">|</span>
              <span>${(summary.total_revenue || 0).toLocaleString()} revenue</span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Low confidence warning */}
        {hasRecommendations && hasLowConfidenceRecs && (
          <WarningBanner
            severity="info"
            icon={Info}
            message="Some recommendations are based on limited data. Review confidence levels before acting."
            compact
          />
        )}
        
        {/* Summary badges */}
        {hasRecommendations && (
          <div className="flex items-center gap-2 flex-wrap">
            {summary.recommendations_count?.scale > 0 && (
              <div className="flex items-center gap-1.5 bg-emerald-500/10 border-2 border-emerald-500/40 text-emerald-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <TrendingUp className="h-3 w-3" />
                {summary.recommendations_count.scale} to SCALE
              </div>
            )}
            {summary.recommendations_count?.fix > 0 && (
              <div className="flex items-center gap-1.5 bg-amber-500/10 border-2 border-amber-500/40 text-amber-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <Wrench className="h-3 w-3" />
                {summary.recommendations_count.fix} to FIX
              </div>
            )}
            {summary.recommendations_count?.pause > 0 && (
              <div className="flex items-center gap-1.5 bg-red-500/10 border-2 border-red-500/40 text-red-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <PauseCircle className="h-3 w-3" />
                {summary.recommendations_count.pause} to PAUSE
              </div>
            )}
            {summary.actions_generated > 0 && (
              <div className="flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/30 text-violet-400 px-2.5 py-1 rounded-full text-xs">
                <Play className="h-3 w-3" />
                {summary.actions_generated} actions available
              </div>
            )}
          </div>
        )}
        
        {!hasRecommendations ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-emerald-400/30 mx-auto mb-3" />
            <p className="text-base text-slate-300 font-medium">All Clear</p>
            <p className="text-sm text-slate-400 mt-1">No immediate actions needed</p>
            <p className="text-xs text-slate-500 mt-2">
              All message variants are performing within expected ranges.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {displayRecommendations.map((rec, idx) => (
              <ActionableRecommendationCard
                key={`${rec.message_variant}-${rec.type}-${idx}`}
                recommendation={rec}
                onActionApproved={handleActionApproved}
                onActionDismissed={handleActionDismissed}
                userId={userId}
              />
            ))}
            
            {recommendations.length > maxRecommendations && (
              <div className="text-center pt-2">
                <button className="text-sm text-violet-400 hover:text-violet-300 flex items-center justify-center gap-1 mx-auto">
                  View all {recommendations.length} recommendations
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Thresholds info */}
        {hasRecommendations && (
          <div className="text-xs text-slate-500 border-t border-slate-700/50 pt-3 mt-3">
            <span className="text-slate-400">Thresholds:</span>{' '}
            Top 25% RPL ≥ ${summary.thresholds?.p75_revenue_per_lead?.toLocaleString() || 0} | 
            Bottom 25% RPL ≤ ${summary.thresholds?.p25_revenue_per_lead?.toLocaleString() || 0}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
